import { styled } from '@styledComponents';
import { Box } from '@components/Box';
import { Flex } from '@components/Flex';
import { Button } from '@components/Button';
import { Text } from '@components/typography';
import { Columns } from '@components/Columns';
import { CTALink } from '@components/CTALink';
import { ParticleJoyLogo } from 'src/layout/ParticleJoyLogo';
import {
  JOY_LOGO_WIDTH_MOBILE,
  JOY_LOGO_HEIGHT_MOBILE,
  JOY_LOGO_WIDTH_TABLET,
  JOY_LOGO_WIDTH_DESKTOP,
} from 'src/layout/ParticleJoyLogo.styles';
import { animationTransition } from '@utils/animationTransition';
import { Label2, SmallTextStyles } from '@components/typography/interVariants';
import { GetStartedButton } from '@components/GetStartedButton/GetStartedButton';
import { themeColor, themeMediaQuery, themeSpace, themeVariant } from '@utils/styledTheme.util';
import { TextCarousel } from '@components/TextCarousel';

export const StyledFooter = styled.footer`
  z-index: 1;
  position: relative;
`;

export const FooterMenuContainer = styled(Box)`
  background-color: white;
  padding-bottom: ${themeSpace(8)};
  ${themeMediaQuery('below_sm')} {
    padding-top: 2rem;
    padding-bottom: 4rem;
  }
  ${themeMediaQuery('sm')} {
    background-color: #f7f7f7;
    padding-top: 0;
    padding-bottom: ${themeSpace(11)};
  }
`;

export const FooterColumnContainer = styled(Box)`
  max-width: 100%;
  padding-left: ${themeSpace(7)};
  padding-right: ${themeSpace(7)};
  ${themeMediaQuery('mobile')} {
    padding-left: 0;
    padding-right: 0;
    text-align: center;
  }
  ${themeMediaQuery('tablet')} {
    padding-left: 0;
    padding-right: 0;
    margin-left: auto;
    margin-right: auto;
  }
  ${themeMediaQuery('sm')} {
    padding-left: 0;
    padding-right: 0;
    margin-left: auto;
    margin-right: auto;
  }
`;

export const FooterColumns = styled(Columns)`
  margin-left: auto;
  margin-right: auto;
  flex-direction: column;
  ${themeMediaQuery('below_sm')} {
    max-width: 100%;
  }
  ${themeMediaQuery('tablet')} {
    flex-direction: row;
    column-gap: ${themeSpace(10)};
  }
  ${themeMediaQuery('sm')} {
    flex-direction: row;
    column-gap: ${themeSpace(10)};
  }
`;

export const FooterColumn = styled(Columns.Column)`
  width: 100%;
  text-align: left;
  display: flex;
  ${themeMediaQuery('tablet')} {
    width: 50%;
    display: block;
    padding-left: 0px;
    padding-right: 0px;
  }
  ${themeMediaQuery('sm')} {
    width: 50%;
    display: block;
    padding-left: 0px;
    padding-right: 0px;
  }
`;

export const FooterColumnBottom = styled(FooterColumn)`
  width: 100%;
`;

export const FooterMenuWrapper = styled(Flex)`
  row-gap: ${themeSpace(4)};
  padding-top: ${themeSpace(6)};
  ${themeMediaQuery('sm')} {
    row-gap: 1rem;
    padding-top: ${themeSpace(11)};
  }
`;

export const FooterSubMenuContainer = styled(Flex)`
  justify-content: flex-start;
  margin-top: ${themeSpace(6)};
  ${themeMediaQuery('sm')} {
    justify-content: flex-start;
    margin-top: 0;
    padding-top: ${themeSpace(11)};
  }
`;

export const FooterSubMenuWrapper = styled(Flex)`
  width: 100%;
  row-gap: 1rem;
  padding-top: 1rem;
  ${themeMediaQuery('tablet')} {
    width: 410px;
  }
  ${themeMediaQuery('sm')} {
    width: 410px;
  }
  ${themeMediaQuery('lg')} {
    padding-top: 1.5rem;
  }
`;

export const FooterMenuCTALink = styled(CTALink)`
  font-size: 30px;
  line-height: 40px;
  ${themeMediaQuery('sm')} {
    ${themeVariant('navChild36')}
    line-height: 48px;
  }
  ${themeMediaQuery('lg')} {
    ${themeVariant('hed56')}
  }
`;

export const FooterSubMenuCTALink = styled(CTALink)`
  ${themeVariant('menuNav16')}
  ${themeMediaQuery('lg')} {
    ${themeVariant('dek20')}
  }
`;

export const UniversalFooterWrapper = styled(Box)`
  z-index: 1;
  position: relative;
`;

export const UniversalFooter = styled(Flex)`
  width: 100%;
  margin: auto;
  max-width: 450px;
  row-gap: ${themeSpace(6)};
  ${themeMediaQuery('below_sm')} {
    max-width: 100%;
  }
  ${themeMediaQuery('sm')} {
    max-width: 100%;
    row-gap: ${themeSpace(7)};
  }
`;

export const UniversalFooterContainer = styled(Flex)`
  width: 100%;
  flex-wrap: wrap;
  row-gap: ${themeSpace(6)};
  flex-direction: column;
  align-items: center;
  ${themeMediaQuery('mobile')} {
    justify-content: center;
  }
  ${themeMediaQuery('md')} {
    flex-direction: row;
    align-items: flex-start;
  }
`;

export const UniversalCtaContainer = styled(Flex)`
  display: none;
  width: 100%;
  row-gap: ${themeSpace(3)};
  flex-direction: column;
  ${themeMediaQuery('md')} {
    display: flex;
    width: initial;
    column-gap: ${themeSpace(4)};
    flex-direction: row;
  }
`;

export const UniversalFooterLink = styled(CTALink)`
  line-height: 18px;
  column-gap: ${themeSpace(3)};
  color: ${themeColor('white')};
  &:hover {
    color: ${themeColor('mono3')};
  }
  &:active {
    color: ${themeColor('white')};
  }
  svg {
    color: ${themeColor('white')};
    &:hover {
      color: ${themeColor('mono3')};
    }
    &:active {
      color: ${themeColor('white')};
    }
    transition: ${animationTransition('color')};
  }
`;

export const NewFlagText = styled(Text)`
  font-size: 10px;
  line-height: 11px;
  font-weight: 600;
  padding: ${themeSpace(1)} ${themeSpace(2)};
  background-color: ${themeColor('white')};
  border-radius: ${themeSpace(7)};
  color: ${themeColor('mono1')};
`;

export const FooterNavButton = styled(Button)<{ active?: boolean }>`
  ${themeVariant('menuNav14')};
  height: 28px;
  display: flex;
  align-items: center;
  border-radius: 14px;
  padding: 0 ${themeSpace(3)};
  color: ${themeColor('white')};
  background: ${({ active }) => (active ? 'rgba(255, 255, 255, 0.15)' : 'transparent')};
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    color: ${themeColor('white')};
  }
  &:active {
    background: rgba(255, 255, 255, 0.25);
    color: ${themeColor('white')};
  }
  ${themeMediaQuery('md')} {
    padding: 0 ${themeSpace(4)};
  }
`;

export const FooterNavButtonContent = styled(Flex)`
  align-items: center;
  gap: ${themeSpace(1)};
`;

export const UniversalFooterMenuWrapper = styled(Flex)`
  flex-direction: column;
  row-gap: ${themeSpace(2)};
  ${themeMediaQuery('mobile')} {
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: center;
    column-gap: ${themeSpace(3)};
  }
  ${themeMediaQuery('below_sm')} {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    column-gap: ${themeSpace(4)};
    row-gap: ${themeSpace(3)};
  }
  ${themeMediaQuery('sm')} {
    flex-direction: row;
    column-gap: ${themeSpace(5)};
  }
`;

export const StyledGetStartedButton = styled(GetStartedButton)`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${themeSpace(3)};
  height: 40px;
  padding: 0 ${themeSpace(5)};
  border-radius: 20px;
  font-family: Inter UI, sans-serif;
  font-weight: 600;
  font-size: 15px;
  line-height: 20px;
  letter-spacing: 0;
  border-width: 1px;
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    color: ${themeColor('white')};
    border-color: transparent;
  }
  &:active {
    background: rgba(255, 255, 255, 0.25);
    color: ${themeColor('white')};
    border-color: transparent;
  }
`;

export const StyledFindEventButton = styled(Button)`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${themeSpace(2)};
  height: 40px;
  padding: 0 ${themeSpace(5)};
  border-radius: 20px;
  font-family: Inter UI, sans-serif;
  font-weight: 600;
  font-size: 15px;
  line-height: 20px;
  letter-spacing: 0;
`;

export const StyledButton = styled(Button)<{ isLogin: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  height: ${themeSpace(9)};
  justify-content: center;
  border: ${({ isLogin }) => (isLogin ? '2px solid white' : 'transperant')};
  ${themeMediaQuery('sm')} {
    width: 174px;
    height: auto;
    border: none;
  }
`;

export const FooterCopyrightContainer = styled(UniversalFooterContainer)`
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-between;
  align-items: center;
  column-gap: ${themeSpace(4)};
  box-shadow: 0px -1px 0px 0px ${themeColor('mono7', 0.1)};
  ${themeMediaQuery('mobile')} {
    flex-wrap: wrap;
    justify-content: center;
    row-gap: ${themeSpace(3)};
  }
  ${themeMediaQuery('below_sm')} {
    margin-top: 1rem;
  }
`;

export const CopyrightText = styled(Text)`
  ${themeVariant('legal')};
  color: ${themeColor('mono7', 0.5)};
  ${themeMediaQuery('md')} {
    ${themeVariant('menuNav14')};
  }
`;

export const PrivacyLinksWrapper = styled(Flex)`
  align-items: center;
  column-gap: 12px;
  ${themeMediaQuery('mobile')} {
    flex-wrap: wrap;
    justify-content: center;
    row-gap: ${themeSpace(2)};
  }
`;

export const StyledFooterLink = styled(UniversalFooterLink)<{ noreferrer?: boolean; noopener?: boolean }>`
  ${themeVariant('legal')};
  color: ${themeColor('mono7', 0.5)};
  &:hover {
    color: ${themeColor('mono7')};
  }
  &:active {
    color: ${themeColor('mono7')};
  }
  ${themeMediaQuery('md')} {
    ${themeVariant('menuNav14')};
  }
`;

export const FooterMenuDisplayAboveMobile = styled(UniversalFooterMenuWrapper)`
  display: none;
  ${themeMediaQuery('sm')} {
    display: flex;
  }
`;

export const FooterMenuDisplayOnMobile = styled(UniversalFooterMenuWrapper)`
  display: flex;
  ${themeMediaQuery('sm')} {
    display: none;
  }
`;

export const UniversalFooterSocialMenuWrapper = styled(UniversalFooterMenuWrapper)`
  flex-direction: row;
  align-items: center;
  column-gap: ${themeSpace(5)};
  /* Override JoykitIcon wrapper to match icon size */
  [class*='IconWrapper'] {
    width: 20px !important;
    height: 20px !important;
  }
  svg {
    width: 20px;
    height: 20px;
  }
  ${themeMediaQuery('md')} {
    column-gap: ${themeSpace(5)};
    [class*='IconWrapper'] {
      width: 24px !important;
      height: 24px !important;
    }
    svg {
      width: 24px;
      height: 24px;
    }
  }
  ${themeMediaQuery('lg')} {
    column-gap: ${themeSpace(6)};
    [class*='IconWrapper'] {
      width: 32px !important;
      height: 32px !important;
    }
    svg {
      width: 32px;
      height: 32px;
    }
  }
`;

export const UniversalFooterLogoContainer = styled(Flex)`
  gap: ${themeSpace(6)} calc(${themeSpace(5)} + ${themeSpace(4)});
  flex-direction: row;
  align-items: center;
  ${themeMediaQuery('sm')} {
    flex-direction: row;
  }
`;

export const StyledParticleJoyLogo = styled(ParticleJoyLogo)`
  display: none;
  justify-content: center;
  svg,
  canvas {
    width: ${JOY_LOGO_WIDTH_MOBILE}px;
    height: ${JOY_LOGO_HEIGHT_MOBILE}px;
  }
  ${themeMediaQuery('md')} {
    display: flex;
    svg,
    canvas {
      width: 56px;
      height: initial;
    }
  }
  ${themeMediaQuery('lg')} {
    svg,
    canvas {
      width: ${JOY_LOGO_WIDTH_DESKTOP}px;
    }
  }
`;

export const TradeMarkContainer = styled(Flex)`
  justify-content: center;
  align-items: flex-start;
  background-color: ${themeColor('neutra6')};
  padding: 0.5rem 0;
  gap: ${themeSpace(4)};
  ${themeMediaQuery('below_sm')} {
    align-items: center;
    gap: ${themeSpace(3)};
    padding: 1rem 0;
  }
  ${themeMediaQuery('md')} {
    padding: 0.75rem 0;
  }
  ${themeMediaQuery('lg')} {
    padding: 1.5rem 0;
  }
`;

export const ContentWrapper = styled(Flex)`
  align-items: flex-end;
  flex-wrap: nowrap;
  gap: ${themeSpace(3)};
  transform: scale(0.6);
  ${themeMediaQuery('below_sm')} {
    gap: ${themeSpace(2)};
    transform: scale(1);
  }
  ${themeMediaQuery('md')} {
    transform: scale(0.7);
  }
  ${themeMediaQuery('lg')} {
    transform: scale(0.9);
  }
`;

export const TradeMarkText = styled(Box)`
  color: white;
  font-family: 'Inter UI', sans-serif;
  font-size: 36px;
  font-weight: 400;
  line-height: 1.35;
  letter-spacing: -0.015em;
  ${themeMediaQuery('below_sm')} {
    ${themeVariant('sectionEyeBrow20')}
  }
`;

export const WithJoyText = styled(Box)`
  color: white;
  font-family: 'Austin News Deck Web';
  font-size: 38px;
  font-weight: 500;
  font-style: italic;
  line-height: 1.35;
  white-space: nowrap;
  transform: translateY(-8px);
  ${themeMediaQuery('below_sm')} {
    ${themeVariant('sectionEyeBrow20')}
    font-family: 'Austin News Deck Web';
    font-style: italic;
    font-weight: 500;
  }
`;

export const TrademarkSymbol = styled.span`
  font-family: 'Inter UI', sans-serif;
  font-style: normal;
  font-size: 0.5em;
  font-weight: 400;
  opacity: 0.5;
  vertical-align: super;
  margin-left: 2px;
`;

export const StyledTextCarousel = styled(TextCarousel)`
  min-width: 320px;
  border-bottom: 2px solid ${themeColor('white')};
  padding-bottom: ${themeSpace(2)};
  ${themeMediaQuery('below_sm')} {
    min-width: 175px;
  }
`;

export const LanguageMenuContainer = styled(Flex)`
  cursor: pointer;
  line-height: 18px;
  column-gap: ${themeSpace(3)};
  color: ${themeColor('white')};

  &:hover {
    color: ${themeColor('mono3')};
  }

  &:active {
    color: ${themeColor('white')};
  }

  svg {
    color: ${themeColor('white')};

    &:hover {
      color: ${themeColor('mono3')};
    }

    &:active {
      color: ${themeColor('white')};
    }

    transition: ${animationTransition('color')};
  }

  ${SmallTextStyles};
`;
